import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {handleError, handleSuccess} from '@store/app.slice';
import {VERIFICATION_REQUIRED_EVENT} from '@utils/verification';
import {IStateApp} from '@store/types';
import {helperHandleError} from '../../helpers';
import {api} from '../../api';
import {IConfirmPhone, IProfile, IProfileState} from './types';
import {ProfileApi} from './Profile.api';
import type {AppDispatch} from '@store/index';

const initialState: IProfileState = {
    confirmPhone: null,
    profile: undefined,
};

export const verifyEmail = createAsyncThunk(
    'profile/verifyEmail',
    async ({email}:{email:string}, {dispatch, getState}) => {
        const {app} = getState() as {app: IStateApp };
        try {
            const res = await api.post<{message: string}>('/user/email/verify', {email}, {
                headers : {
                    'Authorization': `${app.token?.token_type} ${app.token?.token}`
                }
            });
            const data = res.data;
            if(res.status === 200) {
                dispatch(handleSuccess(data));
            }
        } catch (e) {
            helperHandleError('registration, appSlice', e, dispatch);
        }
    }
);

export const changeEmail = createAsyncThunk<void, {email: string}, {dispatch: AppDispatch}>(
    'profile/changeEmail',
    async ({email}:{email: string}, {dispatch, getState}) => {
        const {app} = getState() as {app: IStateApp };
        try {
            const res = await api.post<{email: string; verified: boolean; email_required: boolean; delivery_failed?: boolean; message: string}>('/user/email/change', {email}, {
                headers : {
                    'Authorization': `${app.token?.token_type} ${app.token?.token}`
                }
            });
            const data = res.data;
            if(res.status === 200) {
                // Сервер уже сменил адрес, даже если письмо не удалось доставить.
                if ((getState() as {app: IStateApp}).app.token?.token !== app.token?.token) return;
                dispatch(ProfileApi.util.updateQueryData('getUserProfile', {token: app.token}, profile => {
                    profile.email = data.email;
                    profile.email_verified_at = null;
                    profile.email_verification_required = true;
                    profile.email_required = data.email_required;
                    if (profile.free_access) profile.free_access.eligible = false;
                }));
                window.dispatchEvent(new CustomEvent(VERIFICATION_REQUIRED_EVENT, {
                    detail: {token: app.token?.token, deliveryMessage: data.delivery_failed ? data.message : undefined},
                }));
                if (data.delivery_failed) dispatch(handleError({message: data.message, errors: {}}));
                else dispatch(handleSuccess(data));
            }
        } catch (e:any) {
            helperHandleError('registration, appSlice', e, dispatch);
        }
    }
);

export const sendConfirmPhone = createAsyncThunk(
    'profile/confirmPhone',
    async (_, {dispatch, getState}) => {
        const {app} = getState() as { app: IStateApp };

        try {
            const res = await api.get<IConfirmPhone>('/user/phone/verify',  {
                headers : {
                    'Authorization': `${app.token?.token_type} ${app.token?.token}`
                }
            });
            return res.data;
        } catch (e:any) {
            helperHandleError('registration, appSlice', e, dispatch);
        }
    }
);

export const sendChangePhone = createAsyncThunk(
    'profile/sendChangePhone',
    async ({phone}:{phone: string}, {dispatch, getState}) => {
        const {app} = getState() as { app: IStateApp };

        try {
            const res = await api.post<IConfirmPhone>('/user/phone/change',  {phone},{
                headers : {
                    'Authorization': `${app.token?.token_type} ${app.token?.token}`
                }
            });
            return res.data;
        } catch (e:any) {
            helperHandleError('registration, appSlice', e, dispatch);
        }
    }
);

const ProfileSlice = createSlice({
    name: 'profile',
    initialState,
    reducers:{
        clearConfirmPhone: (state, {payload}) => {
            state.confirmPhone = payload;
        },
        setProfile: (state, {payload}: PayloadAction<IProfile>) => {
            state.profile = payload;
        }
    },
    extraReducers: builder => {
        builder.addCase(sendConfirmPhone.fulfilled, (state, {payload}:any) => {
            state.confirmPhone = payload;
        });
        builder.addCase(sendChangePhone.fulfilled, (state, {payload}:any) => {
            state.confirmPhone = payload;
        });

    }
});

export const {clearConfirmPhone} = ProfileSlice.actions;

export default ProfileSlice.reducer;
